/**
 * MobileFieldNav.js — Persistent top nav (fixed header) + hardware back button
 * interceptor. Prevents mobile back gesture from exiting the app; instead
 * navigates to the previous internal route via React Router history.
 *
 * Visible on non-landing / non-public routes. Landing page stays clean.
 */
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CYAN = "#00CFCF";
const INK = "#F5F5F7";
const CHROME = "#A0A0A0";

const PUBLIC_PATHS = ["/", "/login", "/auth/callback", "/desire-studio-preview", "/get-app", "/install", "/partner", "/partners", "/hub", "/gateway", "/apply", "/join", "/work", "/vendor-application", "/vendor-portal", "/clear-unit"];

const bar = {
  position: "fixed", top: 0, left: 0, right: 0, zIndex: 5000,
  background: "rgba(2,10,31,0.92)", backdropFilter: "blur(10px)",
  WebkitBackdropFilter: "blur(10px)", borderBottom: `1px solid ${CHROME}22`,
  padding: "8px 12px", display: "flex", alignItems: "center", gap: 8,
  color: INK, fontSize: 12,
};
const btn = {
  padding: "6px 10px", borderRadius: 8, background: "transparent",
  border: `1px solid ${CHROME}44`, color: INK, fontSize: 11, cursor: "pointer",
  textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4,
};
const btnActive = { ...btn, background: CYAN, color: "#020A1F", border: 0, fontWeight: 700 };

export default function MobileFieldNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const [projects, setProjects] = useState([]);
  const [projectsOpen, setProjectsOpen] = useState(false);

  const isPublic = PUBLIC_PATHS.some(p => location.pathname === p || (p !== "/" && location.pathname.startsWith(p + "/")));

  useEffect(() => {
    if (isPublic) return;
    fetch(`${BACKEND_URL}/api/mf/projects`)
      .then(r => r.json())
      .then(d => setProjects(d.projects || []))
      .catch(() => {});
  }, [isPublic]);

  // Ask the dashboard to step back through its tabs first; returns true if handled.
  const stepBackInDashboard = () => {
    if (location.pathname !== "/dashboard") return false;
    const ev = new CustomEvent("mf-nav-back", { detail: { handled: false } });
    window.dispatchEvent(ev);
    return ev.detail.handled;
  };

  const goBack = () => {
    if (stepBackInDashboard()) return;
    const idx = window.history.state?.idx ?? 0;
    if (idx > 0) navigate(-1);
    else if (location.pathname !== "/dashboard") navigate("/dashboard");
  };

  // Hardware back on /dashboard: walk tab history instead of leaving the app
  useEffect(() => {
    if (location.pathname !== "/dashboard") return;
    if (!window.history.state?.mfNav) window.history.pushState({ ...(window.history.state || {}), mfNav: true }, "");
    const onPop = () => {
      if (window.location.pathname !== "/dashboard") return;
      stepBackInDashboard();
      window.history.pushState({ ...(window.history.state || {}), mfNav: true }, "");
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  if (isPublic) return null;

  const isCommandCenter = location.pathname.startsWith("/coo-command-center") || location.pathname.startsWith("/coo");
  const isField = location.pathname.startsWith("/field-specialist") || location.pathname.startsWith("/crew");

  const checkIn = async (projectId) => {
    await fetch(`${BACKEND_URL}/api/mf/projects/check-in`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ project_id: projectId }),
    });
    setProjectsOpen(false);
  };

  return (
    <>
      <div style={bar} data-testid="mf-mobile-nav">
        <button style={btn} onClick={goBack} data-testid="mf-nav-back" aria-label="Back">
          ← Back
        </button>
        <Link to="/dashboard" style={btn} data-testid="mf-nav-home">◉ Projects</Link>

        <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
          <Link to="/coo-command-center"
                style={isCommandCenter ? btnActive : btn}
                data-testid="mf-nav-coo">
            COO
          </Link>
          <Link to="/field-specialist"
                style={isField ? btnActive : btn}
                data-testid="mf-nav-field">
            Field
          </Link>
          <button style={btn} onClick={() => setProjectsOpen(v => !v)}
                  data-testid="mf-nav-projects-toggle">
            ⌗
          </button>
        </div>
      </div>

      {projectsOpen && (
        <div data-testid="mf-nav-projects-drawer"
             style={{
               position: "fixed", top: 46, right: 8, zIndex: 5001,
               background: "rgba(2,10,31,0.98)", border: `1px solid ${CYAN}44`,
               borderRadius: 10, padding: 12, maxWidth: 320, minWidth: 260,
               boxShadow: "0 12px 40px rgba(0,0,0,0.6)",
             }}>
          <div style={{ fontSize: 10, letterSpacing: "0.2em", color: CYAN, marginBottom: 8 }}>
            MY PROJECTS · RECENT
          </div>
          {projects.length === 0 && (
            <div style={{ fontSize: 12, color: CHROME }}>Loading…</div>
          )}
          {projects.map(p => (
            <div key={p.project_id}
                 data-testid={`mf-project-${p.project_id}`}
                 style={{ padding: "8px 6px", borderTop: `1px solid ${CHROME}22`, fontSize: 12 }}>
              <div style={{ fontWeight: 700, color: INK }}>{p.label}</div>
              <div style={{ color: CHROME, fontSize: 10 }}>
                {p.last_check_in ? `Last check-in: ${new Date(p.last_check_in).toLocaleString()}` : "No check-ins yet"}
              </div>
              <button style={{ ...btn, marginTop: 4, fontSize: 10 }}
                      onClick={() => checkIn(p.project_id)}
                      data-testid={`mf-project-checkin-${p.project_id}`}>
                Check In →
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Reserve vertical space so the fixed bar doesn't cover content */}
      <div style={{ height: 44 }} aria-hidden="true" />
    </>
  );
}
